"use client";

/**
 * Turns the visitor's analytics answer into session replay being on or off.
 * Renders nothing — it exists so an app wires replay by mounting one component.
 *
 * Gates replay from the start only in an app whose `configureSentryClient` call
 * passes `gateReplayOnConsent`. Without it replay already ran from `init`, so
 * this stops it on a refusal but cannot un-record what came before.
 */
import { useEffect } from "react";

import { useConsent } from "../consent/ConsentProvider";
import { applyReplayConsent } from "./replay";

export function ConsentGatedReplay({
  suppressed = false,
}: {
  /**
   * Off for this page whatever the visitor answered, for an app with pages that
   * must never be recorded. Read here rather than by a second caller of
   * `applyReplayConsent`: two callers would race over the interrupted-mode latch
   * in `replay.ts`, and a `start()` landing inside a `stop()` leaves replay dead
   * until the page reloads.
   */
  suppressed?: boolean;
}) {
  const { ready, allows } = useConsent();
  const analyticsAllowed = allows("analytics");

  useEffect(() => {
    // Before the stored choice is read `allows` answers false for everyone, and
    // acting on that would read as a refusal from a visitor who already agreed.
    if (!ready) return;
    applyReplayConsent(analyticsAllowed && !suppressed);
  }, [ready, analyticsAllowed, suppressed]);

  return null;
}
